import { useEffect, useRef, useState } from 'react';

const cards = [
  {
    tag: 'Our Model',
    featured: false,
    icon: 'ri-percent-line',
    title: 'Performance-Based Fees — Not Just Base Management',
    desc: 'Our incentive fee structure is tied directly to RevPAR improvement, NOI targets, and cost benchmarks as part of our Hotel Management Service. If we don\'t hit the numbers, our fees reflect that. No padding. No excuses.',
  },
  {
    tag: 'Partnership First',
    featured: true,
    icon: 'ri-team-line',
    title: 'You Get a Dedicated Partner Team — Not a Rotating Account Manager',
    desc: 'Every 360 client is assigned a senior partner team with direct accountability. You have one point of contact, full transparency into operations, and weekly performance reporting — always.',
  },
  {
    tag: 'End-to-End',
    featured: false,
    icon: 'ri-links-line',
    title: 'Operations → Asset → Contract — One Integrated Platform',
    desc: 'We bridge hotel operations, asset optimization, and contract advisory under one roof — combining Hotel Management Service, Hotel Revenue Management Service, and Hotel Brand Management Service for the independent hotel segment. That\'s your edge.',
  },
];

export default function TheDifference() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.2 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section className="bg-gray-950 py-24 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-2xl mx-auto mb-16">
          <p className="text-xs font-light text-amber-500 uppercase tracking-widest mb-4" style={{ letterSpacing: '0.25em' }}>
            The 360 Difference
          </p>
          <h2
            className="text-4xl lg:text-5xl font-extralight text-white leading-tight"
          >
            Why We're Built Different
          </h2>
        </div>

        {/* Cards */}
        <div ref={ref} className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-stretch">
          {cards.map((card, i) => (
            <div
              key={i}
              className={`relative rounded-2xl p-8 flex flex-col transition-all duration-700 ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              } ${
                card.featured
                  ? 'bg-amber-600 text-white scale-105 lg:scale-110 z-10'
                  : 'bg-white/5 border border-white/10 text-white hover:bg-white/8 hover:border-white/20'
              }`}
              style={{ transitionDelay: `${i * 150}ms` }}
            >
              {/* Featured badge */}
              {card.featured && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="inline-flex items-center space-x-1 px-4 py-1 bg-white text-amber-600 text-xs font-normal rounded-full whitespace-nowrap">
                    <i className="ri-star-fill text-xs"></i>
                    <span>Featured</span>
                  </span>
                </div>
              )}

              {/* Tag */}
              <span
                className={`text-xs font-light uppercase tracking-widest mb-6 ${
                  card.featured ? 'text-white/70' : 'text-amber-500'
                }`}
                style={{ letterSpacing: '0.2em' }}
              >
                {card.tag}
              </span>

              {/* Icon */}
              <div className={`w-12 h-12 flex items-center justify-center rounded-xl mb-6 ${
                card.featured ? 'bg-white/20' : 'bg-amber-600/10'
              }`}>
                <i className={`${card.icon} text-2xl ${card.featured ? 'text-white' : 'text-amber-500'}`}></i>
              </div>

              {/* Content */}
              <h3
                className={`text-xl font-light mb-4 leading-snug ${
                  card.featured ? 'text-white' : 'text-white'
                }`}
              >
                {card.title}
              </h3>
              <p className={`text-sm font-light leading-relaxed flex-1 ${
                card.featured ? 'text-white/80' : 'text-gray-400'
              }`}>
                {card.desc}
              </p>

              {/* Bottom line */}
              {!card.featured && (
                <div className="mt-6 pt-6 border-t border-white/10">
                  <span className="text-xs font-light text-amber-500 uppercase tracking-widest" style={{ letterSpacing: '0.15em' }}>
                    360 Hospitality Group
                  </span>
                </div>
              )}
              {card.featured && (
                <div className="mt-6 pt-6 border-t border-white/20">
                  <span className="text-xs font-light text-white/60 uppercase tracking-widest" style={{ letterSpacing: '0.15em' }}>
                    360 Hospitality Group
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
